import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Loader2, RefreshCw, Trash2, Clock, MapPin, Package,
  CalendarX, Lightbulb, Gift, Mail, Hourglass, CheckCircle2,
  XCircle, Tag, Search, Building2, Briefcase, FileText,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ============================================================
// Two sub-views:
//   • Expired Jobs (existing flow)
//   • Leads · Form Submitted – No Map (new auto-coupon flow)
// We render a tab bar at the top and switch between the two.
// ============================================================

const AdminOpportunities = () => {
  const { getAuthHeader } = useAuth();
  const [view, setView] = useState('leads'); // 'leads' | 'jobs'

  return (
    <div className="p-6 space-y-6" data-testid="admin-opportunities">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Lightbulb size={24} className="text-amber-500" /> Opportunities
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Win-back surfaces for leads who didn&apos;t draw a map and clients whose approval window expired.
          </p>
        </div>
      </div>

      {/* Sub-tab bar */}
      <div className="border-b border-gray-200 flex gap-1">
        <button
          onClick={() => setView('leads')}
          className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 transition-colors ${
            view === 'leads'
              ? 'border-purple-600 text-purple-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="opp-tab-leads"
        >
          <FileText size={14} className="inline mr-1.5" />
          Leads · Form Submitted, No Map
        </button>
        <button
          onClick={() => setView('jobs')}
          className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 transition-colors ${
            view === 'jobs'
              ? 'border-amber-600 text-amber-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="opp-tab-jobs"
        >
          <Briefcase size={14} className="inline mr-1.5" />
          Expired Jobs
        </button>
      </div>

      {view === 'leads' && <LeadOpportunitiesView getAuthHeader={getAuthHeader} />}
      {view === 'jobs' && <ExpiredJobsView getAuthHeader={getAuthHeader} />}
    </div>
  );
};

// ============================================================
// NEW: Lead opportunities — form submitted but no polygon drawn
// ============================================================

const LEAD_STATE_BADGE = {
  waiting:   { label: 'Waiting (<48h)', cls: 'bg-gray-100 text-gray-700' },
  eligible:  { label: 'Eligible to send', cls: 'bg-amber-100 text-amber-700' },
  sent:      { label: 'Coupon sent', cls: 'bg-blue-100 text-blue-700' },
  redeemed:  { label: 'Redeemed', cls: 'bg-emerald-100 text-emerald-700' },
  expired:   { label: 'Expired', cls: 'bg-red-100 text-red-700' },
  none:      { label: '—', cls: 'bg-gray-100 text-gray-500' },
};

const LeadOpportunitiesView = ({ getAuthHeader }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [runningTick, setRunningTick] = useState(false);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/lead-opportunities`, getAuthHeader());
      setLeads(res.data?.opportunities || []);
    } catch {
      toast.error('Failed to load lead opportunities');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleSendCouponNow = async (leadId, email) => {
    if (!window.confirm(`Send the $100 off coupon to ${email} right now? (Bypasses the 48-hour wait.)`)) return;
    setActionId(leadId);
    try {
      const res = await axios.post(
        `${API}/api/admin/lead-opportunities/${leadId}/send-coupon-now`, {},
        getAuthHeader(),
      );
      const code = res.data?.sent_to?.[0]?.code;
      toast.success(`Coupon ${code} sent to ${email}.`);
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send coupon');
    } finally {
      setActionId(null);
    }
  };

  const handleRunTick = async () => {
    setRunningTick(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/lead-opportunities/dispatch-now`, {},
        getAuthHeader(),
      );
      const sent = res.data?.dispatched ?? 0;
      const considered = res.data?.leads_considered ?? 0;
      toast.success(
        sent
          ? `Dispatched ${sent} coupon${sent === 1 ? '' : 's'} (considered ${considered}).`
          : `No eligible leads right now (considered ${considered}).`,
      );
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Tick failed');
    } finally {
      setRunningTick(false);
    }
  };

  const filtered = leads.filter(l => {
    if (filter !== 'all' && l.coupon_state !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      const blob = `${l.email} ${l.name} ${l.business_name} ${l.target_area}`.toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });

  const counts = leads.reduce((acc, l) => {
    acc[l.coupon_state] = (acc[l.coupon_state] || 0) + 1;
    return acc;
  }, {});
  const filters = [
    { key: 'all',      label: 'All',       count: leads.length },
    { key: 'waiting',  label: 'Waiting',   count: counts.waiting || 0 },
    { key: 'eligible', label: 'Eligible',  count: counts.eligible || 0 },
    { key: 'sent',     label: 'Sent',      count: counts.sent || 0 },
    { key: 'redeemed', label: 'Redeemed',  count: counts.redeemed || 0 },
    { key: 'expired',  label: 'Expired',   count: counts.expired || 0 },
  ];

  return (
    <div className="space-y-4" data-testid="lead-opportunities-view">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex flex-wrap gap-2" data-testid="lead-opp-filters">
          {filters.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filter === f.key
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`lead-opp-filter-${f.key}`}
            >
              {f.label} {f.count > 0 && `(${f.count})`}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Search email or business…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 text-sm h-9 w-64"
              data-testid="lead-opp-search"
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleRunTick}
            disabled={runningTick}
            className="text-purple-700 border-purple-200 hover:bg-purple-50"
            data-testid="lead-opp-run-tick"
            title="Manually run the hourly dispatcher (idempotent — only sends to leads ≥48h old without a coupon)"
          >
            {runningTick ? <Loader2 size={14} className="animate-spin mr-1" /> : <Hourglass size={14} className="mr-1" />}
            Run check now
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center p-16"><Loader2 size={28} className="animate-spin text-purple-500" /></div>
      ) : filtered.length > 0 ? (
        <div className="space-y-2">
          {filtered.map(l => {
            const stateBadge = LEAD_STATE_BADGE[l.coupon_state] || LEAD_STATE_BADGE.none;
            const canSendNow = l.coupon_state === 'waiting' || l.coupon_state === 'eligible';
            return (
              <Card
                key={l.id}
                className="p-4 border-0 shadow-sm"
                data-testid={`lead-opportunity-${l.id}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-semibold text-gray-900 text-sm">
                        {l.name || l.email}
                      </p>
                      <Badge className={`${stateBadge.cls} text-xs`} data-testid={`lead-state-${l.id}`}>
                        {stateBadge.label}
                      </Badge>
                      {l.business_name && (
                        <Badge className="bg-purple-50 text-purple-700 text-xs border border-purple-200">
                          <Building2 size={10} className="mr-0.5" /> {l.business_name}
                        </Badge>
                      )}
                      {l.coupon_code && (
                        <Badge className="bg-emerald-50 text-emerald-700 text-xs border border-emerald-200 font-mono" data-testid={`coupon-code-${l.id}`}>
                          <Tag size={10} className="mr-0.5" /> {l.coupon_code}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Mail size={12} /> {l.email}</span>
                      {typeof l.hours_since_form === 'number' && (
                        <span className="flex items-center gap-1">
                          <Clock size={12} /> {l.hours_since_form < 1
                            ? '< 1 hour ago'
                            : `${Math.floor(l.hours_since_form)} hours ago`}
                        </span>
                      )}
                      {l.target_area && (
                        <span className="flex items-center gap-1"><MapPin size={12} /> {l.target_area}</span>
                      )}
                      {l.delivery_quantity && (
                        <span className="flex items-center gap-1"><Package size={12} /> {l.delivery_quantity}</span>
                      )}
                    </div>
                    {l.coupon_sent_at && (
                      <p className="text-xs text-gray-400 mt-1">
                        Coupon sent {new Date(l.coupon_sent_at).toLocaleString()}
                        {l.coupon_expires_at && (
                          <> · expires {new Date(l.coupon_expires_at).toLocaleString()}</>
                        )}
                        {l.coupon_redeemed_at && (
                          <> · <span className="text-emerald-700 font-medium">redeemed {new Date(l.coupon_redeemed_at).toLocaleString()}</span></>
                        )}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {canSendNow && (
                      <Button
                        size="sm"
                        onClick={() => handleSendCouponNow(l.id, l.email)}
                        disabled={actionId === l.id}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white"
                        data-testid={`send-coupon-now-${l.id}`}
                      >
                        {actionId === l.id
                          ? <Loader2 size={14} className="animate-spin mr-1" />
                          : <Gift size={14} className="mr-1" />}
                        Send $100 Off Now
                      </Button>
                    )}
                    {l.coupon_state === 'sent' && (
                      <Badge className="bg-blue-50 text-blue-700 border border-blue-200 text-xs" title="Customer must redeem the coupon by clicking the email link and drawing their map.">
                        Awaiting redemption
                      </Badge>
                    )}
                    {l.coupon_state === 'redeemed' && (
                      <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs">
                        <CheckCircle2 size={10} className="mr-0.5" /> Map drawn
                      </Badge>
                    )}
                    {l.coupon_state === 'expired' && (
                      <Badge className="bg-red-50 text-red-700 border border-red-200 text-xs">
                        <XCircle size={10} className="mr-0.5" /> Expired
                      </Badge>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="p-16 border-0 shadow-sm text-center">
          <FileText size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 text-lg font-medium">
            {filter === 'all' ? 'No lead opportunities right now' : `No leads matching "${filter}"`}
          </p>
          <p className="text-sm text-gray-400 mt-1">
            Customers who submit the Single Business Order Form but don't draw a map appear here.
            They auto-receive a $100 off coupon after 48 hours.
          </p>
        </Card>
      )}
    </div>
  );
};

// ============================================================
// EXISTING: Expired Jobs view — moved verbatim into its own component
// ============================================================

const ExpiredJobsView = ({ getAuthHeader }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);

  const fetchOpportunities = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/opportunities`, getAuthHeader());
      setJobs(res.data);
    } catch { toast.error('Failed to load opportunities'); }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { fetchOpportunities(); }, [fetchOpportunities]);

  const handleReactivate = async (jobId) => {
    setActionId(jobId);
    try {
      const res = await axios.post(`${API}/api/admin/opportunities/${jobId}/reactivate`, {}, getAuthHeader());
      const needsAdmin = res.data?.needs_admin_approval;
      const needsClient = res.data?.needs_client_approval;
      let msg = 'Job reactivated with a fresh 7-day approval window';
      if (needsAdmin && needsClient) {
        msg = 'Reactivated — both admin AND client approvals are now re-open.';
      } else if (needsClient) {
        msg = 'Reactivated — a fresh approval email link is ready for the client.';
      } else if (needsAdmin) {
        msg = 'Reactivated — client already approved; only admin approval is pending now.';
      }
      toast.success(msg);
      if (res.data?.new_token) {
        const approvalUrl = `${window.location.origin}/client/approve/${res.data.new_token}`;
        try { await navigator.clipboard.writeText(approvalUrl); } catch (_) { /* ignore */ }
        toast.info('New client approval link copied to clipboard.', { duration: 8000 });
      }
      fetchOpportunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate');
    } finally { setActionId(null); }
  };

  const handleDelete = async (jobId, jobTitle) => {
    if (!window.confirm(`Move "${jobTitle}" to Deleted Jobs?`)) return;
    setActionId(jobId);
    try {
      await axios.delete(`${API}/api/admin/opportunities/${jobId}`, getAuthHeader());
      toast.success('Opportunity removed');
      fetchOpportunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally { setActionId(null); }
  };

  const handleSendOffer = async (jobId, clientName) => {
    if (!window.confirm(`Send a $100 off special offer to the client for this job? This will also reactivate the approval link for 7 days.`)) return;
    setActionId(jobId);
    try {
      const res = await axios.post(`${API}/api/admin/opportunities/${jobId}/send-offer`, {}, getAuthHeader());
      toast.success(`Special offer sent to ${clientName || 'client'}! Coupon: ${res.data.coupon_code}`);
      fetchOpportunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send offer');
    } finally { setActionId(null); }
  };

  const getDaysAgo = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const days = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
    return days === 0 ? 'Today' : days === 1 ? '1 day ago' : `${days} days ago`;
  };

  return (
    <div className="space-y-4" data-testid="expired-jobs-view">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Expired approval requests. Reactivate to send a fresh 7-day approval link to the client.
        </p>
        {jobs.length > 0 && (
          <Badge className="bg-amber-100 text-amber-700 text-sm px-3 py-1">
            {jobs.length} expired
          </Badge>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center p-16"><Loader2 size={28} className="animate-spin text-amber-500" /></div>
      ) : jobs.length > 0 ? (
        <div className="space-y-3">
          {jobs.map(job => (
            <Card key={job.id} className="p-5 border-0 shadow-sm" data-testid={`opportunity-${job.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <p className="font-semibold text-gray-900">{job.title || job.job_title}</p>
                    <Badge className="bg-red-100 text-red-600 text-xs">
                      <CalendarX size={10} className="mr-0.5" /> Expired
                    </Badge>
                    {job.job_number && (
                      <span className="text-xs text-gray-400 font-mono">#{job.job_number}</span>
                    )}
                    {job.admin_approved ? (
                      <Badge className="bg-emerald-100 text-emerald-700 text-[10px] border border-emerald-200" data-testid={`admin-approved-${job.id}`}>
                        ✓ Admin approved
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800 text-[10px] border border-amber-300" data-testid={`needs-admin-${job.id}`}>
                        ⚠ Needs admin approval
                      </Badge>
                    )}
                    {job.client_approved ? (
                      <Badge className="bg-emerald-100 text-emerald-700 text-[10px] border border-emerald-200" data-testid={`client-approved-${job.id}`}>
                        ✓ Client approved
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800 text-[10px] border border-amber-300" data-testid={`needs-client-${job.id}`}>
                        ⚠ Needs client approval
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                    {job.client && <span className="font-medium">{job.client}</span>}
                    {job.location && <span className="flex items-center gap-1"><MapPin size={12} /> {job.location}</span>}
                    <span className="flex items-center gap-1"><Package size={12} /> {job.deliveries} deliveries</span>
                    <span className="flex items-center gap-1">
                      <Clock size={12} /> Expired {getDaysAgo(job.expired_at)}
                    </span>
                    {job.created_at && (
                      <span className="text-gray-400">Created {getDaysAgo(job.created_at)}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    onClick={() => handleSendOffer(job.id, job.client_contact_name || job.client)}
                    disabled={actionId === job.id}
                    className="bg-purple-600 hover:bg-purple-500 text-white"
                    data-testid={`send-offer-${job.id}`}
                  >
                    {actionId === job.id ? <Loader2 size={14} className="animate-spin mr-1" /> : <Gift size={14} className="mr-1" />}
                    Send $100 Off
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleReactivate(job.id)}
                    disabled={actionId === job.id}
                    className="bg-amber-500 hover:bg-amber-400 text-white"
                    data-testid={`reactivate-${job.id}`}
                  >
                    {actionId === job.id ? <Loader2 size={14} className="animate-spin mr-1" /> : <RefreshCw size={14} className="mr-1" />}
                    Reactivate
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(job.id, job.title || job.job_title)}
                    disabled={actionId === job.id}
                    className="text-red-500 border-red-200 hover:bg-red-50"
                    data-testid={`delete-opportunity-${job.id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-16 border-0 shadow-sm text-center">
          <Lightbulb size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 text-lg font-medium">No expired opportunities</p>
          <p className="text-sm text-gray-400 mt-1">Pending approval jobs that expire after 7 days will appear here</p>
        </Card>
      )}
    </div>
  );
};

export default AdminOpportunities;
